/**
 * Client ของ `GET /v1/executive-summary` (P2-10d) ตาม payload contract ที่ freeze
 * ไว้ใน `apps/api/src/cap_erp_api/modules/reporting/{router,schemas}.py` (D-106)
 *
 * **เส้นเดียว อ่านอย่างเดียว** — ไม่มี `POST`/`PATCH`/`DELETE` ให้ client นี้เรียก
 * เลยแม้แต่เส้นเดียว เพราะตารางนี้ไม่มีเส้นทางเขียน (D-106 ข้อ 5) การมีฟังก์ชันเขียน
 * อยู่ในไฟล์นี้แม้แต่ตัวเดียวจะเป็นการโฆษณาความสามารถที่เซิร์ฟเวอร์ไม่เคยรับ
 *
 * หลักการเดียวกับ client อื่นทุกตัวในโปรเจกต์นี้ (ดู `lib/forecasts/client.ts`):
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ไม่มีข้อมูล"** — หน้าว่างกับ
 *   โหลดไม่สำเร็จต้องแยกกันให้ขาด (invariant: "an empty period is an empty state,
 *   not an error" — และ "never silently show the previous period's numbers")
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import {
  isMetricUnit,
  type ExecutiveSummaryItem,
  type ExecutiveSummaryListPage,
  type SnapshotScope,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

export const EXECUTIVE_SUMMARY_PATH = '/v1/executive-summary';

/**
 * พารามิเตอร์ของ `GET /v1/executive-summary` ที่ contract รับ — และไม่มีตัวที่หก
 *
 * `scope`, `date_from`, `date_to` **บังคับทั้งสามตัว ไม่มี default** ตรงกับที่
 * `router.py` ประกาศไว้ตรง ๆ — ส่งไม่ครบคือคำขอที่ผิดรูปตั้งแต่ต้น ไม่ใช่คำขอที่
 * เซิร์ฟเวอร์จะเดาค่าให้
 */
export interface ExecutiveSummaryListQuery {
  readonly scope: SnapshotScope;
  /** `YYYY-MM-DD` ล้วน — วันแรกของช่วง รวมวันนั้นด้วย */
  readonly date_from: string;
  /** `YYYY-MM-DD` ล้วน — วันสุดท้ายของช่วง รวมวันนั้นด้วย */
  readonly date_to: string;
  readonly metric_code?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function buildExecutiveSummaryListQuery(query: ExecutiveSummaryListQuery): string {
  const params = new URLSearchParams();
  params.append('scope', query.scope);
  params.append('date_from', query.date_from);
  params.append('date_to', query.date_to);
  const metricCode = query.metric_code?.trim() ?? '';
  if (metricCode !== '') {
    params.append('metric_code', metricCode);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  return `?${params.toString()}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /** `details.rule` — วิธีที่ contract แยก `EXECUTIVE_SUMMARY_*` ออกจากกัน */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract ของเส้นนี้นิยามไว้ — **ไม่มี `not-found` และไม่มี
 * `conflict`**: เส้นนี้ไม่มี `404` เลย (หน้าต่างที่ไม่มีแถวคือหน้าว่าง ไม่ใช่ของหาย)
 * และไม่มี `409` เพราะไม่มีเส้นทางเขียนให้ชนกัน (`_RESPONSES` ใน `router.py` มีแค่
 * `400`, `401`, `403`, `422`)
 *
 * `rule-violation` (`422`) คือที่อยู่ของสามกฎที่แพงที่สุดของเส้นนี้: ช่วงกลับด้าน
 * ช่วงกว้างเกิน และ metric นอกวงคำศัพท์ — ทั้งสามต้องแยกจาก `forbidden` เพราะไม่ใช่
 * เรื่องสิทธิ์เลยแม้แต่น้อย
 */
export type ExecutiveSummaryFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ExecutiveSummaryListResult =
  | { readonly status: 'ready'; readonly page: ExecutiveSummaryListPage }
  | ExecutiveSummaryFailure;

export interface ExecutiveSummaryRequestOptions {
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `apiFetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** `null` เป็นค่าที่ถูกต้อง (= ยอดรวมทั้งบริษัท) — คีย์ที่หายไปหรือผิดชนิดคือผิดรูป */
function nullablePlantId(value: unknown): string | null | undefined {
  if (value === null) {
    return null;
  }
  return typeof value === 'string' && value.trim() !== '' ? value : undefined;
}

const CALENDAR_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` ล้วน — ไม่ผ่าน `Date` แม้แต่ครั้งเดียว เหตุผลเดียวกับ forecast */
function parseCalendarDate(value: unknown): string | null {
  const text = requiredString(value);
  return text !== null && CALENDAR_DATE_PATTERN.test(text) ? text : null;
}

/**
 * หนึ่งแถว — **หน่วยอ่านจาก `metric_unit` ของแถวเสมอ ไม่ map จาก `metric_code`**
 * (invariant ของ Work Order นี้) แถวที่มี unit นอกสามค่าที่ `MetricUnit`
 * ฝั่งเซิร์ฟเวอร์ประกาศไว้ถือเป็นผิดรูป — วงคำศัพท์เปลี่ยนที่เซิร์ฟเวอร์ หน้าจอนี้
 * ต้องรู้ตัวว่าเก่ากว่า ไม่ใช่เดาหน่วยเอาเอง
 */
export function parseExecutiveSummaryItem(raw: unknown): ExecutiveSummaryItem | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const plantId = nullablePlantId(record.plant_id);
  const metricCode = requiredString(record.metric_code);
  const metricUnitRaw = requiredString(record.metric_unit);
  const metricValue = requiredString(record.metric_value);
  const periodStart = parseCalendarDate(record.period_start);
  const periodEnd = parseCalendarDate(record.period_end);

  if (
    plantId === undefined ||
    metricCode === null ||
    metricUnitRaw === null ||
    !isMetricUnit(metricUnitRaw) ||
    metricValue === null ||
    periodStart === null ||
    periodEnd === null
  ) {
    return null;
  }

  return {
    plant_id: plantId,
    metric_code: metricCode,
    metric_unit: metricUnitRaw,
    metric_value: metricValue,
    period_start: periodStart,
    period_end: periodEnd,
  };
}

function parseCursor(raw: unknown): string | null | undefined {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw !== 'string') {
    return undefined;
  }
  return raw.trim() === '' ? null : raw;
}

export function parseExecutiveSummaryListPage(raw: unknown): ExecutiveSummaryListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: ExecutiveSummaryItem[] = [];
  for (const entry of record.items) {
    const parsed = parseExecutiveSummaryItem(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return EMPTY_ENVELOPE;
  }
}

/**
 * `GET /v1/executive-summary` — **เห็นทุกโรงงานเสมอ ไม่กรองตาม plant scope ของ
 * ผู้เรียก** (D-106 ข้อ 4, `docs/SECURITY.md`) ข้อยกเว้นนี้อยู่ที่เซิร์ฟเวอร์ทั้งหมด
 * — client ตัวนี้แค่ส่ง query ที่ผู้ใช้เลือกและอ่านคำตอบตามที่ได้มา ไม่มีตรรกะกรอง
 * ซ้ำที่นี่เลย
 */
export async function fetchExecutiveSummary(
  query: ExecutiveSummaryListQuery,
  options: ExecutiveSummaryRequestOptions = {},
): Promise<ExecutiveSummaryListResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, `${EXECUTIVE_SUMMARY_PATH}${buildExecutiveSummaryListQuery(query)}`);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const page = parseExecutiveSummaryListPage(payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย
 */
export function ruleFromFailure(failure: ExecutiveSummaryFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
